import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// services
import planService from "services/plans.js";
import logger from "utils/logger";

export default function CardPlanTable({ color, onEditPlan, onDeletePlan, onNewPlan }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  // Load plans from API
  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await planService.getPlans();
      setPlans(response.data || []);
      setError(null);
    } catch (err) {
      logger.error('Error loading plans:', err);
      setError('Error al cargar los planes');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadPlans();
  }, []);

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
  };

  // Handle plan deletion
  const handleDeletePlan = async (planId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este plan?')) {
      try {
        await planService.deletePlan(planId);
        await loadPlans(); // Reload plans
        if (onDeletePlan) onDeletePlan(planId);
      } catch (err) {
        logger.error('Error deleting plan:', err);
        const errorMessage = err.response?.data?.message || 'Error al eliminar el plan';
        alert(errorMessage);
      }
    }
  };

  // Filter plans based on search
  const filteredPlans = plans.filter(plan => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      plan.name?.toLowerCase().includes(search) ||
      plan.slug?.toLowerCase().includes(search) ||
      plan.description?.toLowerCase().includes(search)
    );
  });

  // Format price - get from configurations
  const formatPrice = (plan) => {
    const price = plan.configurations?.price || '0.00';
    return parseFloat(price).toFixed(2);
  };

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Gestión de Planes
              </h3>
            </div>
            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
              {onNewPlan && (
                <button
                  className="bg-indigo-500 text-white active:bg-indigo-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={onNewPlan}
                >
                  Nuevo Plan
                </button>
              )}
            </div>
          </div>

          {/* Search Filter */}
          <div className="flex flex-wrap items-center mt-4 space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar plan..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className={
                  "text-sm px-5 py-2 placeholder-blueGray-300 relative bg-white rounded border border-blueGray-300 outline-none focus:outline-none focus:ring w-full " +
                  (color === "light" ? "text-blueGray-600" : "text-white bg-lightBlue-800 border-lightBlue-700")
                }
              />
            </div>
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          {/* Plans table */}
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Nombre
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Precio
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Usuarios
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Estado
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Configuraciones
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    Cargando planes...
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-red-500">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && filteredPlans.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-blueGray-500">
                    No se encontraron planes
                  </td>
                </tr>
              )}

              {!loading && !error && filteredPlans.map((plan) => (
                <tr key={plan.id}>
                  <th className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left flex items-center">
                    <div className="flex flex-col">
                      <span className="font-bold text-blueGray-600">{plan.name}</span>
                      <span className="text-xs text-blueGray-400">{plan.slug}</span>
                    </div>
                  </th>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    ${formatPrice(plan)}
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {plan.users_count || 0}
                    </span>
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {plan.is_active ? (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Activo
                      </span>
                    ) : (
                      <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <div className="flex flex-col space-y-1">
                      {plan.configurations && Object.keys(plan.configurations).length > 0 ? (
                        Object.entries(plan.configurations).slice(0, 3).map(([key, value]) => (
                          <span key={key} className="text-xs text-blueGray-600">
                            <span className="font-semibold">{key}:</span> {value}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-blueGray-400">Sin configuraciones</span>
                      )}
                      {plan.configurations && Object.keys(plan.configurations).length > 3 && (
                        <span className="text-xs text-blueGray-400">
                          +{Object.keys(plan.configurations).length - 3} más
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <div className="flex space-x-2">
                      <button
                        className="text-blue-500 hover:text-blue-700 font-bold"
                        onClick={() => onEditPlan && onEditPlan(plan)}
                        title="Editar plan"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700 font-bold"
                        onClick={() => handleDeletePlan(plan.id)}
                        title="Eliminar plan"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CardPlanTable.defaultProps = {
  color: "light",
};

CardPlanTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  onEditPlan: PropTypes.func,
  onDeletePlan: PropTypes.func,
  onNewPlan: PropTypes.func,
};
